/**
 * ui#87, reshaped by ui#567: pure fallback/format logic for the recipient header's sender identity, so
 * the JSX just renders the derived fields.
 *
 * The header used to be a SENTENCE — "«email» sent you files". It is now the sender's display identity
 * on its own: their account name when they have one (api#462), otherwise the email captured on the
 * create form (ui#87), and nothing at all when the transfer carries neither.
 *
 * Preferring the name is a privacy improvement rather than only a cosmetic one: the email is disclosed
 * to recipients by design, but a name DISPLACES it on screen, so a recipient who does not already have
 * the sender's address is not handed it. Both fields ride the same payload, so "prefer name, else
 * email" has to be a rule here — an api that sends a name still sends the email beside it.
 */
export interface SenderIdentity {
	/**
	 * What the header shows, already trimmed: the name, else the email, else `null` for a transfer with
	 * no attribution at all — in which case the page renders no heading rather than an empty one.
	 */
	display: string | null;
	/**
	 * Avatar initial, taken from {@link display} rather than always from the email, so the letter can
	 * never caption an identity that is not on screen. "T" (Transferify) when there is nothing to show.
	 */
	initial: string;
	/**
	 * The sender's display NAME on its own — never their address, and `null` when the transfer carries
	 * no name (kitecorp/transferify-ui#1693).
	 *
	 * Separate from {@link display} because the two answer different questions. The header asks "who is
	 * this from", and an address is a legitimate answer there — it is disclosed to recipients by design.
	 * The referral offer asks "whose name may I put in a sentence", and an address is not: a promotional
	 * headline reading `someone@example.com invited you` hands out an address the header would only have
	 * shown when there was nothing better, and reads as a mail-merge failure rather than an invitation.
	 *
	 * A `senderName` that IS an address answers `null` too. Account names are user-set and some accounts
	 * set theirs to their email, so preferring the name field is not on its own enough to keep an address
	 * out of the sentence.
	 */
	name: string | null;
}

/**
 * Derives the header identity from the resolve response's optional `senderName` and `senderEmail`.
 *
 * Both arguments are optional-and-nullable because an api that predates either field simply omits it;
 * that path must land on the email, which is exactly what a plain-master backend still sends.
 */
export function deriveSenderIdentity(
	senderName: string | null | undefined,
	senderEmail: string | null | undefined,
): SenderIdentity {
	const trimmedName = senderName?.trim() || null;
	const display = trimmedName || senderEmail?.trim() || null;
	return {
		display,
		initial: display !== null ? display.charAt(0).toUpperCase() : "T",
		name: trimmedName !== null && !looksLikeEmailAddress(trimmedName) ? trimmedName : null,
	};
}

/**
 * Whether a value reads as an email address rather than a person's name.
 *
 * An `@` with something either side of it, which is all the certainty this decision needs: the cost of
 * a false positive is a headline that says "a friend" instead of a name, and the cost of a false
 * negative is publishing an address. Deliberately not an RFC-shaped validator — the api has already
 * accepted whatever this is, so the question here is only how it READS.
 */
function looksLikeEmailAddress(value: string): boolean {
	return /\S+@\S+/u.test(value);
}
